// A label as a small colored chip: the color tints the background and the
// text, mixed with the theme's own colors so it reads on light and dark.
import type { CSSProperties } from "react";
import { type Label, useLabelsOf } from "./queries";

/** The inline style that colors a chip (and the dot beside a name). */
export function chipStyle(color: string): CSSProperties {
  return {
    background: `color-mix(in srgb, ${color} 16%, transparent)`,
    color: `color-mix(in srgb, ${color} 62%, var(--color-fg))`,
  };
}

export function LabelChip({
  label,
  className = "",
}: {
  label: Pick<Label, "name" | "color">;
  className?: string;
}) {
  return (
    <span
      style={chipStyle(label.color)}
      className={`inline-flex h-[18px] min-w-0 max-w-[140px] flex-none items-center rounded-[5px] px-[6px] text-[11px] leading-none ${className}`}
    >
      <span className="truncate">{label.name}</span>
    </span>
  );
}

/** The chips of the labels `ids` (nothing while none is known). */
export function LabelChips({
  ids,
  className = "",
  wrap = true,
}: {
  ids: readonly string[];
  className?: string;
  /** Chips that do not fit go on a new line (false: they are cut off). */
  wrap?: boolean;
}) {
  const labels = useLabelsOf(ids);
  if (labels.length === 0) return null;
  return (
    <span
      className={`flex min-w-0 items-center gap-1 ${wrap ? "flex-wrap" : "flex-nowrap overflow-hidden"} ${className}`}
    >
      {labels.map((l) => (
        <LabelChip key={l.id} label={l} />
      ))}
    </span>
  );
}
